// Shared WebRTC audio helpers.

// Explicit capture constraints — echo cancellation, noise suppression and auto
// gain make support calls dramatically clearer than the bare {audio:true}.
export const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// Enable Opus in-band FEC (recovers from packet loss — big win on mobile) and
// DTX (drops silence to save bandwidth). Returns the SDP unchanged on any error.
export function tuneOpus(sdp) {
  try {
    // Match the whole rtpmap line (incl. the trailing "/2" channel count) so a
    // freshly-inserted fmtp line lands cleanly on its own line.
    const m = sdp.match(/a=rtpmap:(\d+) opus\/48000[^\r\n]*/i);
    if (!m) return sdp;
    const pt = m[1];
    const fmtpRe = new RegExp(`a=fmtp:${pt} ([^\\r\\n]*)`);
    if (fmtpRe.test(sdp)) {
      return sdp.replace(fmtpRe, (_line, params) => {
        let p = params;
        if (!/useinbandfec=/.test(p)) p += ";useinbandfec=1";
        if (!/usedtx=/.test(p)) p += ";usedtx=1";
        return `a=fmtp:${pt} ${p}`;
      });
    }
    return sdp.replace(m[0], `${m[0]}\r\na=fmtp:${pt} useinbandfec=1;usedtx=1`);
  } catch {
    return sdp;
  }
}

// Poll getStats() for the inbound speaking level (0..1) and a coarse quality
// verdict ("good" | "poor") from packet loss. Returns a stop() function.
export function monitorStats(pc, onSample, interval = 500) {
  let prevLost = 0;
  let prevRecv = 0;
  const timer = setInterval(async () => {
    if (!pc || pc.connectionState === "closed") return;
    let level = 0;
    let quality = "good";
    try {
      const stats = await pc.getStats();
      stats.forEach((r) => {
        if (r.type === "inbound-rtp" && (r.kind === "audio" || r.mediaType === "audio")) {
          if (typeof r.audioLevel === "number") level = Math.max(level, r.audioLevel);
          const lost = r.packetsLost || 0;
          const recv = r.packetsReceived || 0;
          const dLost = lost - prevLost;
          const dRecv = recv - prevRecv;
          prevLost = lost;
          prevRecv = recv;
          if (dRecv > 0 && dLost / (dLost + dRecv) > 0.08) quality = "poor";
        }
        if (r.type === "track" && typeof r.audioLevel === "number") {
          level = Math.max(level, r.audioLevel);
        }
      });
    } catch {
      /* ignore */
    }
    onSample({ level: Math.min(1, level * 2.2), quality });
  }, interval);
  return () => clearInterval(timer);
}
